import { color, layout, position, space, variant as styledVariant, system, typography } from 'styled-system';
import { join, values } from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';
import styled, { css, ThemeProvider } from 'styled-components';
import StyledSystemPropTypes from '@styled-system/prop-types';

import { theme as appTheme, globalStyles } from '../../../constants';
import { BUTTON_THEME_COLORS, BUTTON_VARIANTS, BUTTON_SIZES } from './constants';
import { ButtonContent } from './button-content';
import { ButtonElement } from './button-element';
import { coreStyles } from '../../typography/font-styles';
import { LoadingIndicator } from '../../icons/loading-indicator';
import { pStyles } from '../../typography/new/body';

const transition = system({
  transition: true,
});

const buttonWidths = {
  fullWidth: 'width: 100%;',
  fit: 'width: fit-content;',
  fixed: {
    small: 'width: 0px;',
    medium: 'width: 160px;',
    tall: 'width: 240px;',
  },
};

const buttonPadding = {
  fit: {
    tall: 'padding: 23px 72px;',
    medium: 'padding: 18px 45px;',
    small: 'padding: 15px 26px;',
  },
  fixed: {
    tall: 'padding: 23px 25.5px;', // this px is assumed no design token
    medium: 'padding: 18px 25.5px;',
    small: 'padding: 15px 5px;', // this px is assumed no design token
  },
};

const buttonHeight = {
  tall: 'height: 56px;',
  medium: 'height: 44px;',
  small: 'height: 38px;',
};

const buttonTextVariants = {
  tall: 'button-medium',
  medium: 'button-small',
  small: 'button-small',
};

const Button = ({
  children,
  className,
  disabled,
  isFullWidth,
  isLoading,
  loadingText,
  leftContent,
  leftText,
  onClick,
  px,
  py,
  rightText,
  textVariant,
  title,
  type,
  variant,
  themeColor,
  size,
  labelProps,
  widthType,
  ...rest
}) => {
  const classNames = [className];
  if (isLoading) {
    classNames.push('loading');
  }
  const buttonTextVariant = buttonTextVariants?.[size] || buttonTextVariants.medium;

  return (
    <ThemeProvider theme={appTheme}>
      <StyledButton
        {...rest}
        className={join(classNames, ' ')}
        disabled={disabled}
        isFullWidth={isFullWidth}
        onClick={!disabled && !isLoading ? onClick : null}
        px={px}
        py={py}
        textVariant={textVariant}
        title={title}
        type={type}
        variant={`${variant}New`}
        size={size}
        widthType={widthType}
      >
        <ButtonContent
          disabled={disabled}
          isLoading={isLoading}
          leftContent={
            isLoading ? (
              <LoadingIndicator mr="0.5rem" color="currentColor" isLoading={isLoading} variant={variant} />
            ) : (
              leftContent
            )
          }
          leftText={leftText}
          rightText={rightText}
          textVariant={buttonTextVariant}
          labelProps={labelProps}
        >
          {isLoading ? loadingText : children}
        </ButtonContent>
      </StyledButton>
    </ThemeProvider>
  );
};

const variants = styledVariant({
  prop: 'variant',
  scale: 'buttonVariants',
  variants: {
    base: {},
    primary: {},
    secondary: {},
    tertiary: {},
    quaternary: {},
    selectable: {},
    thinPrimary: {},
    link: {},
    action: {},
    actionPrimary: {},
    actionSecondary: {},
    inactive: {},
  },
});

const StyledButton = styled(ButtonElement).withConfig({
  shouldForwardProp: (prop, defaultValidatorFn) => {
    return (
      !['isFullWidth', 'variant', 'textVariant', 'transition', 'size', 'widthType'].includes(prop) &&
      defaultValidatorFn(prop)
    );
  },
})`
  ${({ textVariant, variant }) =>
    variant === 'link' &&
    css`
      ${coreStyles};
      ${pStyles(typeof textVariant === 'number' ? 'standard' : textVariant)};
    `}

  ${props => variants({ ...props, variant: 'baseNew' })}
  ${color};

  font-family: ${globalStyles.baseFontFamily};
  align-items: center;
  display: inline-flex;
  justify-content: center;
  line-height: 1;
  position: relative;
  text-align: center;
  text-decoration: none;
  white-space: nowrap;


  ${({ isFullWidth }) =>
    isFullWidth &&
    css`
      width: 100%;
    `}

  ${({ size, widthType }) => {
    // Apply height based on size
    const heightStyle = buttonHeight[size] || buttonHeight.medium;

    // Apply width based on widthType and potentially size
    let widthStyle = '';
    if (widthType === 'full') {
      widthStyle = buttonWidths.fullWidth;
    } else if (widthType === 'fit') {
      widthStyle = buttonWidths.fit;
    } else if (widthType === 'fixed') {
      widthStyle = buttonWidths.fixed[size] || buttonWidths.fixed.medium;
    }

    // Apply padding based on widthType and size
    let paddingStyle = '';
    if (buttonPadding?.[widthType]?.[size]) {
      paddingStyle = buttonPadding[widthType][size];
    } else {
      paddingStyle = buttonPadding?.[widthType]?.medium || buttonPadding.fit.medium;
    }

    return css`
      ${heightStyle}
      ${widthStyle}
      ${paddingStyle}
      ${space}
      ${layout}
      ${position}
      ${typography}
      ${transition}
      ${variants}

    `;
  }}
`;

Button.propTypes = {
  ...StyledSystemPropTypes.color,
  ...StyledSystemPropTypes.space,
  ...StyledSystemPropTypes.typography,
  children: PropTypes.node.isRequired,
  disabled: PropTypes.bool,
  isLoading: PropTypes.bool,
  loadingText: PropTypes.string,
  leftContent: PropTypes.node,
  onClick: PropTypes.func,
  textVariant: PropTypes.oneOf([PropTypes.number, PropTypes.string]),
  title: PropTypes.string,
  type: PropTypes.oneOf(['submit', 'reset', 'button']),
  variant: PropTypes.oneOf(values(BUTTON_VARIANTS)),
  themeColor: PropTypes.oneOf(values(BUTTON_THEME_COLORS)),
  size: PropTypes.oneOf(values(BUTTON_SIZES)),
  widthType: PropTypes.oneOf(['full', 'fit', 'fixed']),
  transition: PropTypes.string,
  labelProps: PropTypes.any,
};

Button.defaultProps = {
  disabled: false,
  isLoading: false,
  loadingText: 'Loading',
  leftContent: undefined,
  onClick: undefined,
  textVariant: 'standard',
  title: null,
  type: 'button',
  themeColor: undefined,
  variant: 'primary',
  size: 'medium',
  widthType: 'fit',
  transition: 'all 1s cubic-bezier(0.165, 0.84, 0.44, 1)',
  labelProps: null,
};

export { Button };
